import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import CustomPaginator from '../../components/CustomPaginator';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import SearchFilterBar from '../../components/SearchFilterBar';
import FeesPanelSkeletonLoader from '../../skeletons/FeesPanelSkeletonLoader';
import MarkFeeModal from './parts/MarkFeeModal';
import StudentFeePanel from './parts/StudentFeePanel';
import ProfilePictureHover from '../../components/ProfilePictureHover';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
import useClassesStore from '../../stateManagers/store/useClassesStore';

interface FeeRecord {
  student_id: string;           // ObjectId hex from new API
  user_id: number | null;
  profile_picture_url: string | null;
  student_name: string;
  admission_no?: string | null;
  father_name?: string | null;
  father_no?: string | null;
  mother_name?: string | null;
  mother_no?: string | null;
  class: string;
  class_id?: string | null;
  section: string | null;
  roll_no: number;
  fee_record_id: string | null; // ObjectId hex
  session: string;
  month_year: string;
  receipt_no: string | null;
  paid_date: string | null;
  total_amount: number | null;
  paid_amount: number | null;
  fees_status: string;
  pay_mode: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface PaginationData {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
}

interface CountsData {
  total_students: number;
  paid_students: number;
  unpaid_students: number;
  exempted_students?: number;
}

interface FilterState {
  selectedClass: string;
  selectedSection: string;
  selectedMonth: string;
  selectedStatus: string;
}

const isFilterActive = (filters: FilterState): boolean =>
  filters.selectedClass.trim() !== '' ||
  filters.selectedSection.trim() !== '' ||
  filters.selectedMonth.trim() !== '' ||
  (filters.selectedStatus.trim() !== '' && filters.selectedStatus.toLowerCase() !== 'all');

const getRowKey = (record: FeeRecord): string =>
  record.fee_record_id
    ? `fee-${record.fee_record_id}`
    : `student-${record.student_id}`;

// ─── Month helpers ─────────────────────────────────────────────────────────
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const monthNumberToName = (month?: number | null): string | null => {
  if (month == null || month < 1 || month > 12) return null;
  return MONTH_NAMES[month - 1];
};

export const buildSessionMonths = (startMonth: string, endMonth: string): string[] => {
  const startIdx = MONTH_NAMES.findIndex((m) => m.toLowerCase() === startMonth.toLowerCase());
  const endIdx = MONTH_NAMES.findIndex((m) => m.toLowerCase() === endMonth.toLowerCase());
  if (startIdx === -1 || endIdx === -1) return MONTH_NAMES;
  const months: string[] = [];
  let i = startIdx;
  while (true) {
    months.push(MONTH_NAMES[i]);
    if (i === endIdx) break;
    i = (i + 1) % 12;
  }
  return months;
};

export const buildSessionMonthYearMap = (
  startMonth: string,
  endMonth: string,
  session: string,
): Record<string, string> => {
  const months = buildSessionMonths(startMonth, endMonth);
  const years = session.split('-');
  const yearA = years[0] ?? String(new Date().getFullYear());
  const yearB = years[1]
    ? years[1].length === 2
      ? yearA.slice(0, 2) + years[1]
      : years[1]
    : yearA;

  const indices = months.map((m) =>
    MONTH_NAMES.findIndex((mn) => mn.toLowerCase() === m.toLowerCase()),
  );

  const map: Record<string, string> = {};
  let wrapped = false;
  months.forEach((name, i) => {
    if (i > 0 && indices[i] < indices[i - 1]) wrapped = true;
    map[name.toLowerCase()] = wrapped ? yearB : yearA;
  });
  return map;
};

const formatMonthYear = (monthName: string, year: string): string => {
  const map: Record<string, string> = {
    january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
    july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
  };
  const code = map[monthName.toLowerCase().trim()];
  return code ? `${year}-${code}` : '';
};

// ───────────────────────────────────────────────────────────────────────────
const FeesPanel: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [feeRecords, setFeeRecords] = useState<FeeRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // ── School details store ─────────────────────────────────────────────────
  const schoolDetails = useSchoolDetailsStore((s) => s.schoolDetails);
  const schoolLoading = useSchoolDetailsStore((s) => s.loading);

  const currentSession = schoolDetails?.current_session ?? null;
  const SESSION_ID = currentSession?.id ?? null;           // ObjectId hex
  const SESSION = currentSession?.session ?? null;         // e.g. "2025-26"
  const SESSION_START = monthNumberToName(currentSession?.start_month);
  const SESSION_END = monthNumberToName(currentSession?.end_month);

  // ── Classes store ────────────────────────────────────────────────────────
  const { classOptions, fetchClasses } = useClassesStore();

  const sessionMonths =
    SESSION_START && SESSION_END
      ? buildSessionMonths(SESSION_START, SESSION_END)
      : MONTH_NAMES;

  const sessionMonthYearMap: Record<string, string> =
    SESSION_START && SESSION_END && SESSION
      ? buildSessionMonthYearMap(SESSION_START, SESSION_END, SESSION)
      : Object.fromEntries(
          MONTH_NAMES.map((m) => [m.toLowerCase(), String(new Date().getFullYear())]),
        );

  // Side panel / modals
  const [panelStudentId, setPanelStudentId] = useState<string | null>(null);
  const [panelStudentRecord, setPanelStudentRecord] = useState<FeeRecord | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMarkFeeModalOpen, setIsMarkFeeModalOpen] = useState(false);
  const [selectedFeeRecordId, setSelectedFeeRecordId] = useState<string | null>(null);
  const [selectedFeeRecord, setSelectedFeeRecord] = useState<FeeRecord | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [selectedMonthYear, setSelectedMonthYear] = useState<string | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);

  const [paginationData, setPaginationData] = useState<PaginationData>({
    current_page: 1,
    per_page: 10,
    total_items: 0,
    total_pages: 1,
  });
  const [counts, setCounts] = useState<CountsData>({
    total_students: 0,
    paid_students: 0,
    unpaid_students: 0,
  });
  const [filters, setFilters] = useState<FilterState>({
    selectedClass: '',
    selectedSection: '',
    selectedMonth: '',
    selectedStatus: 'All',
  });

  const panelOpen = panelStudentId !== null;

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  // NOTE: No auto-fetch on mount / session load. The panel stays empty
  // until the user picks a class via the filter bar (see fetchFeeRecords
  // guard below) and applies filters.

  const getClassByName = useCallback(
    (className: string) => {
      const normalized = className.toLowerCase().trim();
      return (
        classOptions.find(
          (c) =>
            c.label.toLowerCase().trim() === normalized ||
            c.value.toLowerCase().trim() === normalized,
        ) ?? null
      );
    },
    [classOptions],
  );

  /** Resolve YYYY-MM for the current filters / default session start */
  const resolveMonthYear = useCallback(
    (currentFilters: FilterState): string => {
      if (currentFilters.selectedMonth.trim()) {
        const parts = currentFilters.selectedMonth.trim().split(' ');
        const mnName = parts[0] ?? '';
        const yr =
          parts[1] ??
          sessionMonthYearMap[mnName.toLowerCase()] ??
          (SESSION ? SESSION.split('-')[0] : String(new Date().getFullYear()));
        const my = formatMonthYear(mnName, yr);
        if (my) return my;
      }
      // Default = first month of session
      if (SESSION_START && SESSION) {
        const yr =
          sessionMonthYearMap[SESSION_START.toLowerCase()] ?? SESSION.split('-')[0];
        return formatMonthYear(SESSION_START, yr);
      }
      return `${new Date().getFullYear()}-04`;
    },
    [SESSION, SESSION_START, sessionMonthYearMap],
  );

  // ── Main data fetch using new fee-records APIs ───────────────────────────
  // Requires a class to be selected. Without a class_id, no request is made
  // (neither class-summary nor plain fee-records) — state is simply reset.
  const fetchFeeRecords = useCallback(
    async (currentPage: number, currentFilters: FilterState) => {
      if (!SESSION_ID || !SESSION) return;

      const classObj = currentFilters.selectedClass.trim()
        ? getClassByName(currentFilters.selectedClass)
        : null;
      const classId = classObj?.id ?? null;

      if (!classId) {
        // No class selected — don't hit the API.
        setFeeRecords([]);
        setCounts({
          total_students: 0,
          paid_students: 0,
          unpaid_students: 0,
          exempted_students: 0,
        });
        setPaginationData({
          current_page: 1,
          per_page: 10,
          total_items: 0,
          total_pages: 1,
        });
        return;
      }

      setLoading(true);

      try {
        const monthYear = resolveMonthYear(currentFilters);
        const section = currentFilters.selectedSection.trim();
        const status = currentFilters.selectedStatus.toLowerCase().trim();
        const statusFilter = !status || status === 'all' ? '' : status;

        const qp = [
          `class_id=${classId}`,
          `session_id=${SESSION_ID}`,
          `month_year=${monthYear}`,
        ];
        if (section) qp.push(`section=${section}`);

        const response = await apiClient.get(`/api/fee-records/class-summary?${qp.join('&')}`);

         console.log('Fee summary:', JSON.stringify(response.data, null, 2));
        console.log('Fee summary URL:', `/api/fee-records/class-summary?${qp.join('&')}`);

        if (response.data.status === 'success') {
          let entries: any[] = response.data.data || [];

          // Client-side status filter (API returns all statuses)
          if (statusFilter) {
            entries = entries.filter(
              (e) => (e.fees_status || 'unpaid').toLowerCase() === statusFilter,
            );
          }

          // Map to FeeRecord shape — now enriched with profile picture,
          // admission no, and parent contact details from the updated API.
          const mapped: FeeRecord[] = entries.map((e) => ({
            student_id: e.student_id,
            user_id: null,
            profile_picture_url: e.profile_picture_url || null,
            student_name: e.student_name || '—',
            admission_no: e.admission_no ?? null,
            father_name: e.father_name ?? null,
            father_no: e.father_no ?? null,
            mother_name: e.mother_name ?? null,
            mother_no: e.mother_no ?? null,
            class: classObj?.label || classObj?.value || '',
            class_id: classId,
            section: e.section || null,
            roll_no: e.roll_no ?? 0,
            fee_record_id: e.fee_record_id
              ? String(e.fee_record_id)
              : e.receipt_no
                ? null
                : null,
            session: SESSION,
            month_year: monthYear,
            receipt_no: e.receipt_no ?? null,
            paid_date: e.paid_date ?? null,
            total_amount: e.total_amount ?? null,
            paid_amount: e.paid_amount ?? null,
            fees_status: e.fees_status || 'unpaid',
            pay_mode: e.pay_mode ?? null,
            created_at: null,
            updated_at: null,
          }));

          // Simple client-side pagination for class-summary (API returns full list)
          const perPage = 10;
          const totalItems = mapped.length;
          const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
          const start = (currentPage - 1) * perPage;
          const pageSlice = mapped.slice(start, start + perPage);

          setFeeRecords(pageSlice);
          setPaginationData({
            current_page: currentPage,
            per_page: perPage,
            total_items: totalItems,
            total_pages: totalPages,
          });

          const apiCounts = response.data.counts || {};
          setCounts({
            total_students: apiCounts.total_students ?? totalItems,
            paid_students: apiCounts.paid ?? 0,
            unpaid_students: apiCounts.unpaid ?? 0,
            exempted_students: apiCounts.exempted ?? 0,
          });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: response.data.message || 'Failed to fetch class fee summary',
            life: 5000,
          });
        }
      } catch (err: any) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: err.response?.data?.message || 'Failed to fetch fee records',
          life: 5000,
        });
      } finally {
        setLoading(false);
      }
    },
    [SESSION_ID, SESSION, resolveMonthYear, getClassByName],
  );

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleViewStudent = (record: FeeRecord) => {
    if (panelStudentId === record.student_id) {
      setPanelStudentId(null);
      setPanelStudentRecord(null);
    } else {
      setPanelStudentId(record.student_id);
      setPanelStudentRecord(record);
    }
  };

  const handleMarkPayment = (record: FeeRecord) => {
    setSelectedStudentId(record.student_id);
    setSelectedSession(SESSION);
    setSelectedMonthYear(record.month_year);
    setSelectedClassId(record.class_id ?? null);
    setSelectedFeeRecord(record);
    setIsMarkFeeModalOpen(true);
  };

  const handleDeleteClick = (record: FeeRecord) => {
    if (!record.fee_record_id) return;
    setSelectedFeeRecordId(record.fee_record_id);
    setSelectedFeeRecord(record);
    setIsDeleteModalOpen(true);
  };

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
    setPage(1);
    fetchFeeRecords(1, newFilters);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= paginationData.total_pages && newPage !== page) {
      setPage(newPage);
      fetchFeeRecords(newPage, filters);
    }
  };

  const hasClassFilter = filters.selectedClass.trim() !== '';

  if (schoolLoading) {
    return <FeesPanelSkeletonLoader />;
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        .fees-panel-container {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }
        .fees-content {
          background: white; border-radius: 20px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          border: 1px solid #e5e7eb; overflow: hidden;
        }
        .fees-table-container { overflow-x: auto; }
        .fees-table { width: 100%; border-collapse: collapse; }
        .fees-table thead {
          background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
          border-bottom: 2px solid #e5e7eb;
        }
        .fees-table th {
          padding: 16px 20px; text-align: left;
          font-size: 11px; font-weight: 700; color: #6b7280;
          text-transform: uppercase; letter-spacing: 0.8px;
        }
        .fees-table tbody tr { border-bottom: 1px solid #f3f4f6; transition: all 0.2s; }
        .fees-table tbody tr:hover {
          background: linear-gradient(to right, rgba(65,105,225,0.02) 0%, rgba(65,105,225,0.01) 100%);
        }
        .fees-table tbody tr.row--active {
          background: rgba(65,105,225,0.05) !important;
        }
        .fees-table tbody tr:last-child { border-bottom: none; }
        .fees-table td { padding: 16px 20px; font-size: 13px; color: #1f2937; font-weight: 500; }
        .student-info { display: flex; align-items: center; gap: 12px; }
        .student-details { display: flex; flex-direction: column; gap: 2px; }
        .student-name { font-weight: 600; color: #1f2937; font-size: 13px; }
        .student-meta { font-size: 11px; color: #9ca3af; font-weight: 500; }
        .info-with-icon { display: flex; align-items: center; gap: 8px; }
        .contact-info { display: flex; flex-direction: column; gap: 2px; }
        .contact-name { font-weight: 600; color: #1f2937; font-size: 12.5px; }
        .contact-phone { font-size: 11px; color: #6b7280; font-weight: 500; display: flex; align-items: center; gap: 5px; }
        .status-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 6px 12px; border-radius: 20px;
          font-size: 11px; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .status-badge.paid {
          background: linear-gradient(135deg, rgba(16,185,129,0.1), rgba(5,150,105,0.08));
          color: #059669; border: 1px solid rgba(16,185,129,0.2);
        }
        .status-badge.unpaid {
          background: linear-gradient(135deg, rgba(239,68,68,0.1), rgba(220,38,38,0.08));
          color: #dc2626; border: 1px solid rgba(239,68,68,0.2);
        }
        .status-badge.partial, .status-badge.partially_paid {
          background: linear-gradient(135deg, rgba(245,158,11,0.1), rgba(217,119,7,0.08));
          color: #d97706; border: 1px solid rgba(245,158,11,0.2);
        }
        .status-badge.exempted {
          background: linear-gradient(135deg, rgba(99,102,241,0.1), rgba(79,70,229,0.08));
          color: #4f46e5; border: 1px solid rgba(99,102,241,0.2);
        }
        .empty-state { padding: 80px 20px; text-align: center; }
        .empty-state-icon {
          width: 80px; height: 80px; margin: 0 auto 20px; border-radius: 50%;
          background: linear-gradient(135deg, rgba(65,105,225,0.1), rgba(91,124,230,0.05));
          display: flex; align-items: center; justify-content: center;
        }
        .empty-state h3 { margin: 0 0 8px; font-size: 18px; font-weight: 700; color: #1f2937; }
        .empty-state p  { margin: 0; font-size: 14px; color: #9ca3af; font-weight: 500; }
        .p-button-text:hover { background: #f3f4f6 !important; }
      `}</style>

      <div className="fees-panel-container">
        <GlobalHeaderTitle
          title="Fees Management"
          subtitle={`Manage and track all student fee records for session ${SESSION ?? '—'}`}
          icon="pi pi-dollar"
          gradientFrom="#4169E1"
          gradientTo="#5B7CE6"
        />

        {/* Filter bar */}
        <div
          style={{
            background: 'white',
            borderRadius: 14,
            padding: '10px 14px',
            marginBottom: 16,
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb',
            marginRight: panelOpen ? 436 : 0,
            transition: 'margin-right 0.28s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              marginBottom: 8,
            }}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {SESSION && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '3px 10px',
                    borderRadius: 8,
                    background: 'rgba(65,105,225,0.08)',
                    border: '1px solid rgba(65,105,225,0.2)',
                  }}
                >
                  <i className="pi pi-calendar" style={{ fontSize: 11, color: '#4169E1' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#1e40af' }}>
                    {SESSION}
                  </span>
                </div>
              )}

              {SESSION_START && SESSION_END && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '3px 10px',
                    borderRadius: 8,
                    background: 'rgba(99,102,241,0.07)',
                    border: '1px solid rgba(99,102,241,0.18)',
                  }}
                >
                  <i className="pi pi-clock" style={{ fontSize: 11, color: '#6366f1' }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#4338ca' }}>
                    {SESSION_START} – {SESSION_END}
                  </span>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                {
                  count: counts.total_students,
                  label: 'Total',
                  icon: 'pi-users',
                  color: '#2563eb',
                  boldColor: '#1e40af',
                  bg: 'rgba(59,130,246,0.08)',
                  border: 'rgba(59,130,246,0.2)',
                },
                {
                  count: counts.paid_students,
                  label: 'Paid',
                  icon: 'pi-check-circle',
                  color: '#059669',
                  boldColor: '#047857',
                  bg: 'rgba(16,185,129,0.08)',
                  border: 'rgba(16,185,129,0.2)',
                },
                {
                  count: counts.unpaid_students,
                  label: 'Unpaid',
                  icon: 'pi-times-circle',
                  color: '#dc2626',
                  boldColor: '#b91c1c',
                  bg: 'rgba(239,68,68,0.08)',
                  border: 'rgba(239,68,68,0.2)',
                },
              ].map(({ count, label, icon, color, boldColor, bg, border }) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    borderRadius: 8,
                    background: `linear-gradient(135deg, ${bg} 0%, ${bg} 100%)`,
                    border: `1px solid ${border}`,
                  }}
                >
                  <i className={`pi ${icon}`} style={{ fontSize: 13, color }} />
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: boldColor }}>
                      {count}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                      }}
                    >
                      {label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <SearchFilterBar
            onApplyFilters={handleApplyFilters}
            sessionMonths={sessionMonths}
            session={SESSION}
          />
        </div>

        {/* Table */}
        <div
          className="fees-content"
          style={{
            marginRight: panelOpen ? 436 : 0,
            transition: 'margin-right 0.28s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          {loading ? (
            <FeesPanelSkeletonLoader />
          ) : feeRecords.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <i className="pi pi-inbox" style={{ fontSize: 32, color: '#4169E1' }} />
              </div>
              <h3>No Fee Records Found</h3>
              <p>
                {hasClassFilter
                  ? 'There are no fee records available for the selected filters.'
                  : 'Select a class to view all students (including unpaid) for the chosen month.'}
              </p>
            </div>
          ) : (
            <>
              <div className="fees-table-container">
                <table className="fees-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Class & Section</th>
                      <th>Father's Contact</th>
                      <th>Receipt No</th>
                      <th>Paid Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Payment Mode</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feeRecords.map((record) => {
                      const statusLower = (record.fees_status || '').toLowerCase();
                      const isPaid = statusLower === 'paid';
                      const isPartial =
                        statusLower === 'partial' || statusLower === 'partially_paid';

                      return (
                        <tr
                          key={getRowKey(record)}
                          className={panelStudentId === record.student_id ? 'row--active' : ''}
                        >
                          <td>
                            <div className="student-info">
                              <ProfilePictureHover
                                src={record.profile_picture_url}
                                name={record.student_name}
                                size={38}
                                previewSize={240}
                                borderRadius={10}
                              />
                              <div className="student-details">
                                <span className="student-name">{record.student_name}</span>
                                <span className="student-meta">Roll: {record.roll_no}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{record.class}</div>
                            {record.section && (
                              <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                Section {record.section}
                              </div>
                            )}
                          </td>
                          <td>
                            {record.father_name || record.father_no ? (
                              <div className="contact-info">
                                {record.father_name && (
                                  <span className="contact-name">{record.father_name}</span>
                                )}
                                {record.father_no && (
                                  <span className="contact-phone">
                                    <i className="pi pi-phone" style={{ fontSize: 10, color: '#9ca3af' }} />
                                    {record.father_no}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>—</span>
                            )}
                          </td>
                          <td>
                            <div className="info-with-icon">
                              <i className="pi pi-file" style={{ fontSize: 14, color: '#9ca3af' }} />
                                {record.receipt_no || '—'}
                            </div>
                          </td>
                          <td>
                            {isPaid && record.paid_date ? (
                              <div className="info-with-icon">
                                <i
                                  className="pi pi-calendar"
                                  style={{ fontSize: 14, color: '#9ca3af' }}
                                />
                                {new Date(record.paid_date).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </div>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>—</span>
                            )}
                          </td>
                          <td>
                            {(isPaid || isPartial) && record.paid_amount != null ? (
                              <div className="info-with-icon" style={{ fontWeight: 600 }}>
                                <i
                                  className="pi pi-indian-rupee"
                                  style={{ fontSize: 12, color: '#059669' }}
                                />
                                {Number(record.paid_amount).toLocaleString('en-IN')}
                              </div>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>—</span>
                            )}
                          </td>
                          <td>
                            <span className={`status-badge ${statusLower}`}>
                              <i
                                className={`pi ${
                                  isPaid
                                    ? 'pi-check-circle'
                                    : statusLower === 'exempted'
                                      ? 'pi-shield'
                                      : 'pi-times-circle'
                                }`}
                                style={{ fontSize: 10 }}
                              />
                              {record.fees_status}
                            </span>
                          </td>
                          <td>
                            <div className="info-with-icon">
                              <i
                                className="pi pi-credit-card"
                                style={{ fontSize: 14, color: '#9ca3af' }}
                              />
                              {/* {record.pay_mode?.toUpperCase() ?? 'N/A'} */}
                                {record.pay_mode ? record.pay_mode.toUpperCase() : 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                              <Button
                                icon="pi pi-eye"
                                className="p-button-text p-button-rounded"
                                tooltip={
                                  panelStudentId === record.student_id
                                    ? 'Close Panel'
                                    : 'View Full Details'
                                }
                                tooltipOptions={{ position: 'top' }}
                                style={{
                                  color:
                                    panelStudentId === record.student_id ? '#4169E1' : '#60a5fa',
                                  width: 32,
                                  height: 32,
                                  background:
                                    panelStudentId === record.student_id
                                      ? 'rgba(65,105,225,0.1)'
                                      : 'transparent',
                                  borderRadius: 8,
                                }}
                                onClick={() => handleViewStudent(record)}
                              />
                              <Button
                                icon="pi pi-indian-rupee"
                                className="p-button-text p-button-rounded"
                                tooltip="Mark Payment"
                                tooltipOptions={{ position: 'top' }}
                                style={{ color: '#10b981', width: 32, height: 32 }}
                                onClick={() => handleMarkPayment(record)}
                              />
                              {isPaid && record.fee_record_id && (
                                <Button
                                  icon="pi pi-trash"
                                  className="p-button-text p-button-rounded"
                                  tooltip="Delete"
                                  tooltipOptions={{ position: 'top' }}
                                  style={{ color: '#ef4444', width: 32, height: 32 }}
                                  onClick={() => handleDeleteClick(record)}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {paginationData.total_pages > 1 && (
                <CustomPaginator
                  page={paginationData.current_page}
                  totalPages={paginationData.total_pages}
                  totalRecords={paginationData.total_items}
                  pageSize={paginationData.per_page}
                  onPageChange={handlePageChange}
                  showInfoBar={true}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Student fee detail panel */}
      {panelOpen && (
        <StudentFeePanel
          studentId={panelStudentId}
          session={SESSION ?? ''}
          sessionId={SESSION_ID ?? ''}
          sessionStart={SESSION_START ?? ''}
          sessionEnd={SESSION_END ?? ''}
          classId={panelStudentRecord?.class_id ?? null}
          studentRecord={
            panelStudentRecord
              ? {
                  student_name: panelStudentRecord.student_name,
                  profile_picture_url: panelStudentRecord.profile_picture_url,
                  class: panelStudentRecord.class,
                  section: panelStudentRecord.section,
                  roll_no: panelStudentRecord.roll_no,
                }
              : null
          }
          onClose={() => {
            setPanelStudentId(null);
            setPanelStudentRecord(null);
          }}
          onPaymentSuccess={() => fetchFeeRecords(page, filters)}
        />
      )}

      {/* Delete Modal - commented out as requested */}
      {/* <DeleteFeeModal
        visible={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedFeeRecordId(null);
          setSelectedFeeRecord(null);
        }}
        feeRecordId={selectedFeeRecordId}
        feeRecord={selectedFeeRecord}
        onSuccess={() => fetchFeeRecords(page, filters)}
        toast={toast}
      /> */}

      {/* Mark Fee Modal */}
      <MarkFeeModal
        open={isMarkFeeModalOpen}
        onClose={() => {
          setIsMarkFeeModalOpen(false);
          setSelectedStudentId(null);
          setSelectedSession(null);
          setSelectedMonthYear(null);
          setSelectedClassId(null);
          setSelectedFeeRecord(null);
        }}
        studentId={selectedStudentId}
        session={SESSION}
        sessionId={SESSION_ID}
        monthYear={selectedMonthYear}
        classId={selectedClassId}
        studentInfo={
          selectedFeeRecord
            ? {
                student_name: selectedFeeRecord.student_name,
                class: selectedFeeRecord.class,
                section: selectedFeeRecord.section,
                roll_no: selectedFeeRecord.roll_no,
              }
            : null
        }
        onSuccess={() => fetchFeeRecords(page, filters)}
        toast={toast}
      />
    </>
  );
};

export default FeesPanel;